import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faRobot,
  faDesktop,
  faUser,
  faCog,
  faArrowRight,
  faArrowDown,
  faPlay,
  faStop,
  faChevronRight,
  faChevronDown,
  faCode,
  faFileAlt,
  faWrench,
  faBrain,
  faDatabase,
  faExternalLinkAlt,
  faCopy,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faCommentDots,
  faExpand,
  faCompress
} from '@fortawesome/free-solid-svg-icons';
import yaml from 'js-yaml';
import toast from 'react-hot-toast';
import { promptAPI, toolsAPI } from '../../utils/api';

// Step type configurations
const STEP_TYPES = {
  ui_step: {
    icon: faDesktop,
    label: 'UI Step',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-500',
    textColor: 'text-green-700 dark:text-green-300'
  },
  AI: {
    icon: faRobot,
    label: 'AI Step',
    color: 'from-purple-500 to-pink-600',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    borderColor: 'border-purple-500',
    textColor: 'text-purple-700 dark:text-purple-300'
  },
  Human: {
    icon: faUser,
    label: 'Human Step',
    color: 'from-blue-500 to-cyan-600',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700 dark:text-blue-300'
  },
  Backend: {
    icon: faCog,
    label: 'Backend Step',
    color: 'from-gray-500 to-slate-600',
    bgColor: 'bg-gray-100 dark:bg-gray-900/30',
    borderColor: 'border-gray-500',
    textColor: 'text-gray-700 dark:text-gray-300'
  },
  tool: {
    icon: faWrench,
    label: 'Tool Step',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-700 dark:text-orange-300'
  }
};

// Full Screen Prompt Modal Component
const PromptFullScreenModal = ({ isOpen, onClose, promptData, promptId }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  const promptContent = promptData?.prompt_template || promptData?.prompt_content || promptData?.content || promptData?.system_prompt || '';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-5xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faCommentDots} className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {promptData?.name || 'Prompt Content'}
                </h2>
                <p className="text-white/70 text-sm font-mono">{promptId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(promptContent)}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} />
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Prompt Metadata */}
        {promptData && (
          <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-900 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4 text-sm">
              {promptData.model && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Model:</span>
                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-medium">
                    {promptData.model}
                  </span>
                </div>
              )}
              {promptData.temperature !== undefined && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Temperature:</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{promptData.temperature}</span>
                </div>
              )}
              {promptData.max_output_tokens && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Max Tokens:</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{promptData.max_output_tokens}</span>
                </div>
              )}
              {promptData.tool_enabled !== undefined && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Tools:</span>
                  <span className={`px-2 py-0.5 rounded font-medium ${promptData.tool_enabled ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                    {promptData.tool_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prompt Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 min-h-[300px]">
            <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono leading-relaxed">
              {promptContent || 'No prompt content available'}
            </pre>
          </div>

          {/* Output Schema */}
          {promptData?.output_schema && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center space-x-2">
                <FontAwesomeIcon icon={faCode} className="text-blue-500" />
                <span>Output Schema</span>
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 overflow-x-auto">
                <pre className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                  {typeof promptData.output_schema === 'string'
                    ? promptData.output_schema
                    : JSON.stringify(promptData.output_schema, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Assigned Tools */}
          {promptData?.assigned_tools && promptData.assigned_tools.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center space-x-2">
                <FontAwesomeIcon icon={faWrench} className="text-orange-500" />
                <span>Assigned Tools ({promptData.assigned_tools.length})</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {promptData.assigned_tools.map((tool, idx) => (
                  <span key={idx} className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg text-sm font-medium">
                    {typeof tool === 'string' ? tool : tool.name || tool.tool_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Full Screen Tool Modal Component
const ToolFullScreenModal = ({ isOpen, onClose, toolData, toolName }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen || !toolData) return null;

  // Parse parameters schema
  const parametersSchema = toolData.parameters_json_schema || toolData.parameters || toolData.input_schema;
  const parsedSchema = typeof parametersSchema === 'string' ? JSON.parse(parametersSchema) : parametersSchema;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-5xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faWrench} className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {toolData.tool_name || toolData.name || toolName}
                </h2>
                <p className="text-white/70 text-sm">Tool Definition</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(JSON.stringify(toolData, null, 2))}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} />
                <span>{copied ? 'Copied!' : 'Copy All'}</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Tool Metadata */}
        <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-900 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-4 text-sm">
            {toolData.tool_definition_id && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400">ID:</span>
                <code className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded font-mono text-xs">
                  {toolData.tool_definition_id}
                </code>
              </div>
            )}
            {toolData.backend_implementation_ref && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400">Backend:</span>
                <code className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded font-mono text-xs">
                  {toolData.backend_implementation_ref}
                </code>
              </div>
            )}
            {toolData.required_auth_service_name && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400">Auth:</span>
                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                  {toolData.required_auth_service_name}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Description */}
          {toolData.description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center space-x-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-gray-500" />
                <span>Description</span>
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {toolData.description}
                </p>
              </div>
            </div>
          )}

          {/* Parameters Schema */}
          {parsedSchema && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center space-x-2">
                <FontAwesomeIcon icon={faCode} className="text-blue-500" />
                <span>Parameters Schema</span>
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 overflow-x-auto">
                <pre className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                  {JSON.stringify(parsedSchema, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Parameters Table */}
          {parsedSchema?.properties && Object.keys(parsedSchema.properties).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center space-x-2">
                <FontAwesomeIcon icon={faCog} className="text-purple-500" />
                <span>Parameters Details</span>
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-400 font-medium">Parameter</th>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-400 font-medium">Type</th>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-400 font-medium">Required</th>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-400 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {Object.entries(parsedSchema.properties).map(([paramName, paramDef]) => (
                      <tr key={paramName} className="hover:bg-gray-100 dark:hover:bg-gray-800">
                        <td className="px-4 py-3">
                          <code className="text-orange-600 dark:text-orange-400 font-mono">{paramName}</code>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                            {paramDef.type || 'any'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {parsedSchema.required?.includes(paramName) ? (
                            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                              Required
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded text-xs">
                              Optional
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {paramDef.description || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Full Tool Definition */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center space-x-2">
              <FontAwesomeIcon icon={faDatabase} className="text-green-500" />
              <span>Full Tool Definition</span>
            </h3>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 overflow-x-auto max-h-64 overflow-y-auto">
              <pre className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                {JSON.stringify(toolData, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Step Node Component
const StepNode = ({ stepName, stepData, isStart, isSelected, onClick }) => {
  const stepType = STEP_TYPES[stepData.type] || STEP_TYPES.Backend;
  const actions = stepData.actions || stepData.on || {};
  const actionCount = Object.keys(actions).length;

  return (
    <motion.div
      whileHover={{ scale: 1.02, boxShadow: '0 8px 25px rgba(0,0,0,0.15)' }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200
        ${stepType.bgColor} ${isSelected ? 'ring-4 ring-blue-400 ring-offset-2' : ''}
        ${stepType.borderColor} hover:shadow-lg
        min-w-[200px] max-w-[280px]
      `}
    >
      {/* Start Badge */}
      {isStart && (
        <div className="absolute -top-3 -left-3 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
          <FontAwesomeIcon icon={faPlay} className="text-white text-xs" />
        </div>
      )}

      {/* Step Header */}
      <div className="flex items-start space-x-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stepType.color} flex items-center justify-center shadow-md flex-shrink-0`}>
          <FontAwesomeIcon icon={stepType.icon} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 dark:text-white text-sm truncate" title={stepName}>
            {stepName}
          </h4>
          <p className={`text-xs ${stepType.textColor} font-medium`}>
            {stepType.label}
          </p>
        </div>
      </div>

      {/* Step Info */}
      <div className="mt-3 space-y-1">
        {stepData.name && (
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate" title={stepData.name}>
            {stepData.name}
          </p>
        )}

        {/* AI Step specific info */}
        {stepData.type === 'AI' && (
          <div className="flex items-center space-x-2 text-xs">
            <span className="px-2 py-0.5 bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded-full">
              {stepData.provider || 'openai'}
            </span>
            {stepData.assistant_id && (
              <span className="text-gray-500 truncate" title={stepData.assistant_id}>
                {stepData.assistant_id.substring(0, 12)}...
              </span>
            )}
            {stepData.prompt_id && (
              <span className="px-2 py-0.5 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full">
                Prompt
              </span>
            )}
          </div>
        )}

        {/* UI Step specific info */}
        {stepData.type === 'ui_step' && stepData.component && (
          <div className="flex items-center space-x-2 text-xs">
            <span className="px-2 py-0.5 bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 rounded-full">
              {stepData.component.template_type || 'form'}
            </span>
            {stepData.component.component_name && (
              <span className="text-gray-500 truncate">
                {stepData.component.component_name}
              </span>
            )}
          </div>
        )}

        {/* Tool info */}
        {stepData.tools && stepData.tools.length > 0 && (
          <div className="flex items-center space-x-1 text-xs text-orange-600 dark:text-orange-400">
            <FontAwesomeIcon icon={faWrench} className="text-xs" />
            <span>{stepData.tools.length} tool(s)</span>
          </div>
        )}
      </div>

      {/* Actions Count */}
      {actionCount > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{actionCount} transition{actionCount > 1 ? 's' : ''}</span>
            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Step Detail Panel Component
const StepDetailPanel = ({ stepName, stepData, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [promptData, setPromptData] = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState(null);
  const [toolsData, setToolsData] = useState({});
  const [toolsLoading, setToolsLoading] = useState(false);
  const [expandedPrompt, setExpandedPrompt] = useState(false);
  const [expandedTools, setExpandedTools] = useState({});
  const [showPromptFullScreen, setShowPromptFullScreen] = useState(false);
  const [showToolFullScreen, setShowToolFullScreen] = useState(false);
  const [selectedToolForFullScreen, setSelectedToolForFullScreen] = useState(null);
  const stepType = STEP_TYPES[stepData.type] || STEP_TYPES.Backend;

  // Fetch prompt content when prompt_id exists
  useEffect(() => {
    const fetchPrompt = async () => {
      if (stepData.prompt_id && !stepData.prompt_id.includes('_prompt_id')) {
        setPromptLoading(true);
        setPromptError(null);
        try {
          const response = await promptAPI.getPrompt(stepData.prompt_id);
          // Handle different response structures
          const data = response?.data || response;
          if (data && (data.prompt_id || data.name || data.prompt_template)) {
            setPromptData(data);
          } else if (data?.error) {
            setPromptError(data.error.message || data.error || 'Failed to load prompt');
          } else {
            setPromptData(data);
          }
        } catch (err) {
          console.error('Failed to fetch prompt:', err);
          const errorMsg = err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to load prompt content';
          setPromptError(errorMsg);
        } finally {
          setPromptLoading(false);
        }
      }
    };
    fetchPrompt();
  }, [stepData.prompt_id]);

  // Fetch tool definitions when tools exist
  useEffect(() => {
    const fetchTools = async () => {
      if (stepData.tools && stepData.tools.length > 0) {
        setToolsLoading(true);
        try {
          const response = await toolsAPI.getTools();
          const allTools = response.data?.tools || response.tools || response.data || [];

          // Create a map of tool name to tool data
          const toolMap = {};
          stepData.tools.forEach(toolName => {
            const name = typeof toolName === 'string' ? toolName : toolName.name;
            const foundTool = allTools.find(t =>
              t.name === name || t.tool_name === name || t.function_name === name
            );
            if (foundTool) {
              toolMap[name] = foundTool;
            }
          });
          setToolsData(toolMap);
        } catch (err) {
          console.error('Failed to fetch tools:', err);
        } finally {
          setToolsLoading(false);
        }
      }
    };
    fetchTools();
  }, [stepData.tools]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleToolExpand = (toolName) => {
    setExpandedTools(prev => ({ ...prev, [toolName]: !prev[toolName] }));
  };

  const actions = stepData.actions || stepData.on || {};

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      className="w-[450px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 h-full overflow-y-auto"
    >
      {/* Header */}
      <div className={`p-4 bg-gradient-to-r ${stepType.color} text-white sticky top-0 z-10`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={stepType.icon} className="text-lg" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{stepName}</h3>
              <p className="text-white/80 text-sm">{stepType.label}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Basic Info */}
        {stepData.name && (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Display Name</h4>
            <p className="text-gray-900 dark:text-white">{stepData.name}</p>
          </div>
        )}

        {/* AI Step Details */}
        {stepData.type === 'AI' && (
          <>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 space-y-3">
              <h4 className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase flex items-center space-x-2">
                <FontAwesomeIcon icon={faBrain} />
                <span>AI Configuration</span>
              </h4>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Provider</span>
                  <p className="font-medium text-gray-900 dark:text-white">{stepData.provider || 'openai'}</p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">API</span>
                  <p className="font-medium text-gray-900 dark:text-white">{stepData.api || 'chat'}</p>
                </div>
              </div>

              {stepData.assistant_id && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Assistant ID</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <code className="flex-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs font-mono truncate">
                      {stepData.assistant_id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(stepData.assistant_id)}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} />
                    </button>
                  </div>
                </div>
              )}

              {stepData.model && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Model</span>
                  <p className="font-medium text-gray-900 dark:text-white">{stepData.model}</p>
                </div>
              )}
            </div>

            {/* PROMPT CONTENT - Full Display */}
            {stepData.prompt_id && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase flex items-center space-x-2">
                    <FontAwesomeIcon icon={faCommentDots} />
                    <span>Prompt</span>
                  </h4>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setExpandedPrompt(!expandedPrompt)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {expandedPrompt ? 'Collapse' : 'Expand'}
                    </button>
                    {promptData && (
                      <button
                        onClick={() => setShowPromptFullScreen(true)}
                        className="p-1.5 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
                        title="View Full Screen"
                      >
                        <FontAwesomeIcon icon={faExpand} className="text-xs" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Prompt ID */}
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Prompt ID</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <code className="flex-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-xs font-mono text-blue-800 dark:text-blue-200 truncate">
                      {stepData.prompt_id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(stepData.prompt_id)}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                </div>

                {/* Prompt Content */}
                {promptLoading ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    <span>Loading prompt content...</span>
                  </div>
                ) : promptError ? (
                  <div className="text-sm text-red-500">{promptError}</div>
                ) : promptData ? (
                  <div className="space-y-2">
                    {promptData.name && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">Name</span>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{promptData.name}</p>
                      </div>
                    )}
                    {promptData.model && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">Model</span>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{promptData.model}</p>
                      </div>
                    )}
                    {(promptData.prompt_template || promptData.prompt_content || promptData.content || promptData.system_prompt) && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">Prompt Content</span>
                        <div className={`mt-1 bg-white dark:bg-gray-800 rounded-lg p-2 ${expandedPrompt ? '' : 'max-h-32'} overflow-y-auto`}>
                          <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                            {promptData.prompt_template || promptData.prompt_content || promptData.content || promptData.system_prompt}
                          </pre>
                        </div>
                        <button
                          onClick={() => copyToClipboard(promptData.prompt_template || promptData.prompt_content || promptData.content || promptData.system_prompt)}
                          className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
                        >
                          <FontAwesomeIcon icon={faCopy} />
                          <span>Copy prompt</span>
                        </button>
                      </div>
                    )}
                    {promptData.output_schema && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">Output Schema</span>
                        <pre className="mt-1 text-xs bg-white dark:bg-gray-800 p-2 rounded overflow-x-auto max-h-24 overflow-y-auto">
                          <code className="text-gray-700 dark:text-gray-300">
                            {typeof promptData.output_schema === 'string'
                              ? promptData.output_schema
                              : JSON.stringify(promptData.output_schema, null, 2)}
                          </code>
                        </pre>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Config */}
            {stepData.config && Object.keys(stepData.config).length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 flex items-center space-x-2">
                  <FontAwesomeIcon icon={faCog} />
                  <span>Configuration</span>
                </h4>
                <div className="space-y-2 text-sm">
                  {Object.entries(stepData.config).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">{key}</span>
                      <span className="font-medium text-gray-900 dark:text-white text-right max-w-[200px] truncate" title={typeof value === 'object' ? JSON.stringify(value) : String(value)}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* UI Step Details */}
        {stepData.type === 'ui_step' && stepData.component && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 space-y-3">
            <h4 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase flex items-center space-x-2">
              <FontAwesomeIcon icon={faDesktop} />
              <span>UI Component</span>
            </h4>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs">Template Type</span>
                <p className="font-medium text-gray-900 dark:text-white">{stepData.component.template_type || 'form'}</p>
              </div>
              {stepData.component.component_name && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Component</span>
                  <p className="font-medium text-gray-900 dark:text-white">{stepData.component.component_name}</p>
                </div>
              )}
            </div>

            {stepData.component.metadata && (
              <div className="pt-2 border-t border-green-200 dark:border-green-800">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Metadata</span>
                <div className="mt-1 space-y-1 text-sm">
                  {stepData.component.metadata.displayName && (
                    <p className="font-medium text-gray-900 dark:text-white">
                      {stepData.component.metadata.icon} {stepData.component.metadata.displayName}
                    </p>
                  )}
                  {stepData.component.metadata.description && (
                    <p className="text-gray-600 dark:text-gray-400 text-xs">
                      {stepData.component.metadata.description}
                    </p>
                  )}
                </div>
              </div>
            )}

            {stepData.component.sections && (
              <div className="pt-2 border-t border-green-200 dark:border-green-800">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Form Sections</span>
                <div className="mt-1 space-y-1">
                  {stepData.component.sections.map((section, idx) => (
                    <div key={idx} className="text-sm bg-white dark:bg-gray-800 rounded px-2 py-1">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {section.title || `Section ${idx + 1}`}
                      </span>
                      {section.fields && (
                        <span className="text-gray-500 ml-2">
                          ({section.fields.length} fields)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TOOLS - Full Display */}
        {stepData.tools && stepData.tools.length > 0 && (
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase mb-2 flex items-center space-x-2">
              <FontAwesomeIcon icon={faWrench} />
              <span>Tools ({stepData.tools.length})</span>
              {toolsLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />}
            </h4>
            <div className="space-y-2">
              {stepData.tools.map((tool, idx) => {
                const toolName = typeof tool === 'string' ? tool : tool.name || tool.tool_name;
                const toolDetails = toolsData[toolName];
                const isExpanded = expandedTools[toolName];

                return (
                  <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                    {/* Tool Header */}
                    <div
                      className="p-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
                      onClick={() => toggleToolExpand(toolName)}
                    >
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faWrench} className="text-orange-500 text-xs" />
                        <span className="font-medium text-gray-900 dark:text-white text-sm">{toolName}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {toolDetails && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedToolForFullScreen({ name: toolName, data: toolDetails });
                              setShowToolFullScreen(true);
                            }}
                            className="p-1.5 bg-orange-100 dark:bg-orange-800 text-orange-600 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-700 transition-colors"
                            title="View Full Screen"
                          >
                            <FontAwesomeIcon icon={faExpand} className="text-xs" />
                          </button>
                        )}
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className={`text-gray-400 text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Tool Details (Expanded) */}
                    {isExpanded && toolDetails && (
                      <div className="px-2 pb-2 border-t border-gray-100 dark:border-gray-700 space-y-2">
                        {toolDetails.description && (
                          <div className="pt-2">
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Description</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">{toolDetails.description}</p>
                          </div>
                        )}

                        {toolDetails.function_name && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Function</span>
                            <code className="block text-xs bg-gray-100 dark:bg-gray-900 p-1 rounded mt-1 text-orange-600 dark:text-orange-400">
                              {toolDetails.function_name}
                            </code>
                          </div>
                        )}

                        {toolDetails.parameters && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Parameters</span>
                            <pre className="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded mt-1 overflow-x-auto max-h-32 overflow-y-auto">
                              <code className="text-gray-700 dark:text-gray-300">
                                {typeof toolDetails.parameters === 'string'
                                  ? toolDetails.parameters
                                  : JSON.stringify(toolDetails.parameters, null, 2)}
                              </code>
                            </pre>
                          </div>
                        )}

                        {toolDetails.tool_definition && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Tool Definition</span>
                            <pre className="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded mt-1 overflow-x-auto max-h-40 overflow-y-auto">
                              <code className="text-gray-700 dark:text-gray-300">
                                {typeof toolDetails.tool_definition === 'string'
                                  ? toolDetails.tool_definition
                                  : JSON.stringify(toolDetails.tool_definition, null, 2)}
                              </code>
                            </pre>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tool Details (Expanded but no data found) */}
                    {isExpanded && !toolDetails && !toolsLoading && (
                      <div className="px-2 pb-2 border-t border-gray-100 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400 pt-2 italic">
                          Tool definition not found in database
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions/Transitions */}
        {Object.keys(actions).length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase mb-2 flex items-center space-x-2">
              <FontAwesomeIcon icon={faArrowRight} />
              <span>Transitions</span>
            </h4>
            <div className="space-y-2">
              {Object.entries(actions).map(([action, target]) => (
                <div key={action} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg px-3 py-2 text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">{action}</span>
                  <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                    <span className="font-mono text-xs">{target}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Raw YAML */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 flex items-center space-x-2">
            <FontAwesomeIcon icon={faCode} />
            <span>Raw Configuration</span>
          </h4>
          <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto max-h-48 overflow-y-auto">
            <code className="text-gray-700 dark:text-gray-300">
              {yaml.dump({ [stepName]: stepData }, { indent: 2 })}
            </code>
          </pre>
        </div>
      </div>

      {/* Full Screen Prompt Modal */}
      <AnimatePresence>
        {showPromptFullScreen && (
          <PromptFullScreenModal
            isOpen={showPromptFullScreen}
            onClose={() => setShowPromptFullScreen(false)}
            promptData={promptData}
            promptId={stepData.prompt_id}
          />
        )}
      </AnimatePresence>

      {/* Full Screen Tool Modal */}
      <AnimatePresence>
        {showToolFullScreen && selectedToolForFullScreen && (
          <ToolFullScreenModal
            isOpen={showToolFullScreen}
            onClose={() => {
              setShowToolFullScreen(false);
              setSelectedToolForFullScreen(null);
            }}
            toolData={selectedToolForFullScreen.data}
            toolName={selectedToolForFullScreen.name}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Workflow Viewer Modal Component
const WorkflowViewerModal = ({ workflow, isOpen, onClose }) => {
  const [parsedWorkflow, setParsedWorkflow] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && workflow?.workflow_yaml) {
      try {
        const parsed = yaml.load(workflow.workflow_yaml);
        setParsedWorkflow(parsed);
        setError(null);
      } catch (err) {
        console.error('Failed to parse workflow YAML:', err);
        setError(err.message);
      }
    }
  }, [isOpen, workflow]);

  // Organize steps into levels for flowchart layout
  const stepLevels = useMemo(() => {
    if (!parsedWorkflow?.steps) return [];

    const steps = parsedWorkflow.steps;
    const startStep = parsedWorkflow.start_step || Object.keys(steps)[0];
    const visited = new Set();
    const levels = [];

    const getNextSteps = (stepName) => {
      const step = steps[stepName];
      if (!step) return [];
      const actions = step.actions || step.on || {};
      return Object.values(actions).filter(target => target && target !== 'end' && steps[target]);
    };

    // BFS to organize steps by level
    let currentLevel = [startStep];
    while (currentLevel.length > 0) {
      const levelSteps = currentLevel.filter(s => !visited.has(s) && steps[s]);
      if (levelSteps.length === 0) break;

      levels.push(levelSteps);
      levelSteps.forEach(s => visited.add(s));

      const nextLevel = [];
      levelSteps.forEach(stepName => {
        getNextSteps(stepName).forEach(next => {
          if (!visited.has(next) && !nextLevel.includes(next)) {
            nextLevel.push(next);
          }
        });
      });
      currentLevel = nextLevel;
    }

    // Add any unvisited steps
    const unvisited = Object.keys(steps).filter(s => !visited.has(s));
    if (unvisited.length > 0) {
      levels.push(unvisited);
    }

    return levels;
  }, [parsedWorkflow]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="flex-1 bg-gray-50 dark:bg-gray-900 m-4 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <FontAwesomeIcon icon={faFileAlt} className="text-white text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {workflow?.name || 'Workflow Viewer'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {parsedWorkflow?.description || workflow?.description || 'View workflow steps and configuration'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {parsedWorkflow && (
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-lg">
                  <span>{Object.keys(parsedWorkflow.steps || {}).length} steps</span>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <span>Start: {parsedWorkflow.start_step || 'N/A'}</span>
                </div>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Flowchart Area */}
          <div className="flex-1 overflow-auto p-6">
            {error ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-2xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Failed to Parse Workflow
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                  {error}
                </p>
              </div>
            ) : !parsedWorkflow ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="min-h-full">
                {/* Start Node */}
                <div className="flex justify-center mb-4">
                  <div className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full font-semibold shadow-lg flex items-center space-x-2">
                    <FontAwesomeIcon icon={faPlay} />
                    <span>START</span>
                  </div>
                </div>

                {/* Step Levels */}
                <div className="space-y-4">
                  {stepLevels.map((level, levelIdx) => (
                    <div key={levelIdx}>
                      {/* Arrow Connector from previous level or START */}
                      <div className="flex justify-center mb-4">
                        <div className="flex flex-col items-center w-full">
                          {/* Vertical line down */}
                          <div className={`w-0.5 h-6 bg-gradient-to-b ${levelIdx === 0 ? 'from-green-500 to-blue-400' : 'from-blue-400 to-blue-500'}`}></div>

                          {/* If multiple steps, show horizontal branching */}
                          {level.length > 1 ? (
                            <div className="relative w-full flex justify-center">
                              {/* Horizontal line */}
                              <div className="absolute top-0 h-0.5 bg-blue-500" style={{ width: `${Math.min(level.length * 220, 800)}px` }}></div>

                              {/* Arrow heads for each step */}
                              <div className="flex justify-center gap-6" style={{ width: `${Math.min(level.length * 220, 800)}px` }}>
                                {level.map((_, idx) => (
                                  <div key={idx} className="flex flex-col items-center" style={{ width: '200px' }}>
                                    <div className="w-0.5 h-4 bg-blue-500"></div>
                                    <FontAwesomeIcon
                                      icon={faChevronDown}
                                      className="text-blue-500 text-sm"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            /* Single step - just show arrow */
                            <div className="flex flex-col items-center">
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className="text-blue-500 text-lg"
                              />
                              <div className="w-0.5 h-2 bg-blue-500"></div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Steps in this level */}
                      <div className="flex flex-wrap justify-center gap-6">
                        {level.map((stepName) => (
                          <div key={stepName} className="relative">
                            <StepNode
                              stepName={stepName}
                              stepData={parsedWorkflow.steps[stepName]}
                              isStart={stepName === parsedWorkflow.start_step}
                              isSelected={selectedStep === stepName}
                              onClick={() => setSelectedStep(stepName)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Arrow to End Node - handle multiple steps at last level */}
                <div className="flex justify-center mt-4">
                  <div className="flex flex-col items-center w-full">
                    {stepLevels.length > 0 && stepLevels[stepLevels.length - 1].length > 1 ? (
                      /* Multiple steps converging to END */
                      <div className="relative w-full flex justify-center">
                        <div className="flex justify-center gap-6" style={{ width: `${Math.min(stepLevels[stepLevels.length - 1].length * 220, 800)}px` }}>
                          {stepLevels[stepLevels.length - 1].map((_, idx) => (
                            <div key={idx} className="flex flex-col items-center" style={{ width: '200px' }}>
                              <div className="w-0.5 h-4 bg-red-400"></div>
                            </div>
                          ))}
                        </div>
                        {/* Horizontal line connecting them */}
                        <div className="absolute bottom-0 h-0.5 bg-red-400" style={{ width: `${Math.min(stepLevels[stepLevels.length - 1].length * 220, 800)}px` }}></div>
                      </div>
                    ) : null}

                    {/* Final arrow down to END */}
                    <div className="flex flex-col items-center">
                      <div className="w-0.5 h-6 bg-gradient-to-b from-red-400 to-red-500"></div>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="text-red-500 text-lg"
                      />
                      <div className="w-0.5 h-2 bg-red-500"></div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-full font-semibold shadow-lg flex items-center space-x-2">
                    <FontAwesomeIcon icon={faStop} />
                    <span>END</span>
                  </div>
                </div>

                {/* Click hint */}
                {!selectedStep && (
                  <div className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400">
                    Click on any step to view details
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step Detail Panel */}
          <AnimatePresence>
            {selectedStep && parsedWorkflow?.steps?.[selectedStep] && (
              <StepDetailPanel
                stepName={selectedStep}
                stepData={parsedWorkflow.steps[selectedStep]}
                onClose={() => setSelectedStep(null)}
              />
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WorkflowViewerModal;
